// Rollover tab (admin ClientDetail) — everything from PRIOR periods that was
// never invoiced and will therefore land on the NEXT invoice.
//
// These amounts are deliberately NOT part of MTD (MTD only sums the current
// period), so without this view they're invisible until billing day. Filters
// mirror services/billing.py exactly, so the totals here equal the invoice
// preview's rollover column.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertTriangle, Info, CheckCircle2 } from 'lucide-react'
import { getClientRollover, clearRolloverItems } from '../lib/api'
import { ToastContainer, useToast } from './Toast'

const money = (n) => {
  const v = Number(n) || 0
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`
}
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—')

function Section({ title, source, rows, columns, emptyText, selected, onToggle, onToggleAll, onClear, onUndo, clearing }) {
  const ids = rows.filter(r => r.id && !r.invoice_id).map(r => r.id)   // cleared rows aren't re-selectable
  const sel = ids.filter(id => selected.has(id))
  const allOn = ids.length > 0 && sel.length === ids.length

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onClear(source, sel)}
            disabled={clearing || sel.length === 0}
            title={sel.length === 0 ? 'Tick one or more rows to clear them' : ''}
            className="btn-secondary text-xs py-1 px-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {clearing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Clear{sel.length > 0 ? ` ${sel.length}` : ''}
          </button>
          <span className="text-xs text-slate-400">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-5 text-sm text-slate-400 text-center">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-100">
              <tr className="text-left">
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allOn} disabled={ids.length === 0}
                    onChange={() => onToggleAll(ids, !allOn)}
                    className="w-4 h-4 rounded border-slate-400 accent-blue-600" />
                </th>
                {columns.map(c => (
                  <th key={c.key} className={`px-4 py-2 ${c.right ? 'text-right' : ''}`}>{c.label}</th>
                ))}
                <th className="px-4 py-2">Billed on</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={r.id || i} className={
                  r.invoice_id ? 'bg-emerald-50/40 text-slate-400'
                    : selected.has(r.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50'}>
                  <td className="px-3 py-2">
                    {!r.invoice_id && (
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => onToggle(r.id)}
                        className="w-4 h-4 rounded border-slate-400 accent-blue-600" />
                    )}
                  </td>
                  {columns.map(c => (
                    <td key={c.key} className={`px-4 py-2 ${c.right ? 'text-right tabular-nums' : ''} ${c.mono ? 'font-mono text-xs' : ''}`}>
                      {c.render ? c.render(r) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                  <td className="px-4 py-2">
                    {r.invoice_id ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="badge bg-emerald-100 text-emerald-700">{r.invoice_label || 'billed'}</span>
                        <button onClick={() => onUndo(source, [r.id])}
                          className="text-xs text-slate-400 hover:text-red-500 underline">undo</button>
                      </span>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function RolloverTab({ clientId }) {
  const qc = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [selected, setSelected] = useState(new Set())
  const [invoiceId, setInvoiceId] = useState('')
  const [showCleared, setShowCleared] = useState(false)   // off by default — rollover view is the point

  const { data, isLoading } = useQuery({
    queryKey: ['client-rollover', clientId, showCleared],
    queryFn: () => getClientRollover(clientId, { include_cleared: showCleared }),
    enabled: !!clientId,
  })

  const clearMut = useMutation({
    mutationFn: ({ source, ids }) => clearRolloverItems(clientId, { source, ids, invoiceId: invoiceId || null }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['client-rollover', clientId] })
      setSelected(new Set())
      addToast({ type: 'success', message: `Cleared ${res.updated} item${res.updated === 1 ? '' : 's'} — removed from rollover.` })
    },
    onError: (e) => addToast({ type: 'error', message: e.response?.data?.detail || 'Clear failed.' }),
  })

  const undoMut = useMutation({
    mutationFn: ({ source, ids }) => clearRolloverItems(clientId, { source, ids, invoiceId: null }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['client-rollover', clientId] })
      addToast({ type: 'success', message: `Restored ${res.updated} item${res.updated === 1 ? '' : 's'} to rollover.` })
    },
    onError: (e) => addToast({ type: 'error', message: e.response?.data?.detail || 'Undo failed.' }),
  })

  function handleUndo(source, ids) {
    if (!window.confirm('Put this back into rollover? It will appear on the next invoice again.')) return
    undoMut.mutate({ source, ids })
  }

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleAll(ids, on) {
    setSelected(prev => {
      const next = new Set(prev)
      ids.forEach(id => (on ? next.add(id) : next.delete(id)))
      return next
    })
  }
  function handleClear(source, ids) {
    if (!invoiceId) {
      addToast({ type: 'error', message: 'Pick the invoice these were billed on first.' })
      return
    }
    const inv = (data.invoices || []).find(i => i.id === invoiceId)
    const label = inv ? (inv.xero_invoice_number || inv.invoice_number || 'selected invoice') : 'selected invoice'
    if (!window.confirm(
      `Clear ${ids.length} item${ids.length === 1 ? '' : 's'} against ${label}?\n\n` +
      `They'll be marked as already billed and will NOT appear on the next invoice.`
    )) return
    clearMut.mutate({ source, ids })
  }

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" /></div>
  if (!data) return null

  const t = data.totals || {}
  // "Nothing" only when there's genuinely nothing to render — if cleared rows are
  // being shown we still render the tables so the clear is confirmable/undoable.
  const rowCount = (data.orders || []).length + (data.export_fees || []).length
    + (data.discrepancies || []).length + (data.returns || []).length
    + (data.warehouse || []).length + (data.sku_charges || []).length
  const nothing = rowCount === 0
  const sectionProps = {
    selected, onToggle: toggle, onToggleAll: toggleAll,
    onClear: handleClear, onUndo: handleUndo, clearing: clearMut.isPending || undoMut.isPending,
  }

  return (
    <div className="space-y-4">
      {/* MTD note — the whole point of this tab */}
      <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
        <Info size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium">These amounts are NOT included in the MTD total.</p>
          <p className="text-xs text-amber-800 mt-0.5">
            MTD only counts the current period. Everything below is from before{' '}
            <span className="font-mono">{data.period_start}</span> and was never invoiced, so it will be
            added to the next invoice as rollover. Deal with it here rather than at billing time.
          </p>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ['Orders — fulfilment', t.orders_fulfilment],
          ['Orders — shipping',   t.orders_shipping],
          ['Export / duty fees',  t.export_fees],
          ['Discrepancies',       t.discrepancies],
          ['Returns',             t.returns],
          ['Warehouse activity',  t.warehouse],
          ['SKU charges',         t.sku_charges],
        ].map(([label, val]) => (
          <div key={label} className="card p-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-lg font-bold text-slate-800 mt-1 tabular-nums">{money(val)}</p>
          </div>
        ))}
      </div>

      <div className="card p-4 flex items-center justify-between bg-slate-50">
        <span className="text-sm font-semibold text-slate-600">
          Total rolling into next invoice
          <span className="text-xs font-normal text-slate-400 ml-2">({t.orders_count || 0} unbilled orders)</span>
        </span>
        <span className={`text-xl font-bold tabular-nums ${(t.grand_total || 0) >= 0 ? 'text-violet-700' : 'text-emerald-700'}`}>
          {money(t.grand_total)}
        </span>
      </div>

      {!nothing && (
        <div className="card p-3 flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Clear selected against:</label>
          <select className="input text-sm py-1.5 max-w-xs" value={invoiceId}
            onChange={e => setInvoiceId(e.target.value)}>
            <option value="">— choose the invoice they were billed on —</option>
            {(data.invoices || []).map(i => (
              <option key={i.id} value={i.id}>
                {(i.xero_invoice_number || i.invoice_number || 'Invoice')} · {fmtDate(i.date)} · {money(i.total)} · {i.status}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer ml-auto">
            <input type="checkbox" checked={showCleared} onChange={e => setShowCleared(e.target.checked)}
              className="w-4 h-4 rounded border-slate-400 accent-blue-600" />
            Show already-cleared rows
          </label>
          <span className="text-xs text-slate-400 basis-full">
            Tick rows below, then “Clear” on that section. Marks them already-billed so they drop off the next
            invoice — the “Billed on” column then shows which invoice, with an undo.
          </span>
        </div>
      )}

      {nothing ? (
        <div className="card p-8 text-center text-sm text-slate-500">
          Nothing rolling over — everything from prior periods has been invoiced.
        </div>
      ) : (
        <div className="space-y-4">
          <Section
            {...sectionProps}
            source="orders"
            title="Unbilled orders from prior periods"
            rows={data.orders || []}
            emptyText="No unbilled prior orders."
            columns={[
              { key: 'order_number', label: 'Order #', mono: true },
              { key: 'shipped_date', label: 'Shipped', render: r => fmtDate(r.shipped_date || r.order_date) },
              { key: 'carrier',      label: 'Carrier' },
              { key: 'total_fulfilment', label: 'Fulfilment', right: true, render: r => money(r.total_fulfilment) },
              { key: 'shipping_cost',    label: 'Shipping',   right: true, render: r => money(r.shipping_cost) },
              { key: 'total_charged',    label: 'Total',      right: true, render: r => money(r.total_charged) },
            ]}
          />
          <Section
            {...sectionProps}
            source="export_fees"
            title="Export / duty fees"
            rows={data.export_fees || []}
            emptyText="No unbilled export fees."
            columns={[
              { key: 'tracking_number', label: 'Tracking', mono: true },
              { key: 'courier',   label: 'Courier' },
              { key: 'fee_date',  label: 'Fee date', render: r => fmtDate(r.fee_date || r.synced_at) },
              { key: 'total_amount', label: 'Amount', right: true, render: r => money(r.total_amount) },
            ]}
          />
          <Section
            {...sectionProps}
            source="discrepancies"
            title="Postage discrepancies"
            rows={data.discrepancies || []}
            emptyText="No unbilled discrepancies."
            columns={[
              { key: 'order_number', label: 'Order #', mono: true },
              { key: 'created_at',   label: 'Created', render: r => fmtDate(r.created_at) },
              { key: 'source',       label: 'Source' },
              { key: 'notes',        label: 'Notes', render: r => (
                  <span className="text-xs text-slate-500" title={r.notes || ''}>
                    {(r.notes || '').slice(0, 60)}{(r.notes || '').length > 60 ? '…' : ''}
                  </span>) },
              { key: 'additional_charge', label: 'Amount', right: true, render: r => money(r.additional_charge) },
            ]}
          />
          <Section
            {...sectionProps}
            source="returns"
            title="Return labels"
            rows={data.returns || []}
            emptyText="No unbilled return labels."
            columns={[
              { key: 'tracking_number', label: 'Tracking', mono: true },
              { key: 'carrier_code',    label: 'Carrier' },
              { key: 'label_created_at', label: 'Created', render: r => fmtDate(r.label_created_at) },
              { key: 'carrier_cost',    label: 'Cost', right: true, render: r => money(r.carrier_cost) },
            ]}
          />
          <Section
            {...sectionProps}
            source="warehouse"
            title="Warehouse activity"
            rows={data.warehouse || []}
            emptyText="No unbilled warehouse activity."
            columns={[
              { key: 'activity_date', label: 'Date', render: r => fmtDate(r.activity_date) },
              { key: 'activity_type', label: 'Type' },
              { key: 'quantity',      label: 'Qty', right: true },
              { key: 'reference',     label: 'Reference' },
              { key: 'notes',         label: 'Notes', render: r => (
                  <span className="text-xs text-slate-500" title={r.notes || ''}>
                    {(r.notes || '').slice(0, 60)}{(r.notes || '').length > 60 ? '\u2026' : ''}
                  </span>) },
              { key: 'fee',           label: 'Amount', right: true, render: r => money(r.fee) },
            ]}
          />
          <Section
            {...sectionProps}
            source="sku_charges"
            title="SKU charges"
            rows={data.sku_charges || []}
            emptyText="No unbilled SKU charges."
            columns={[
              { key: 'shipped_date', label: 'Shipped', render: r => fmtDate(r.shipped_date) },
              { key: 'sku',          label: 'SKU', mono: true },
              { key: 'quantity',     label: 'Units', right: true },
              // `charge` is resolved server-side off the effective-dated rate
              // card — order_items stores no amount of its own.
              { key: 'charge',       label: 'Amount', right: true, render: r => money(r.charge) },
            ]}
          />
        </div>
      )}

      <p className="flex items-center gap-1.5 text-xs text-slate-400">
        <AlertTriangle size={13} />
        A row appears here whenever it was never stamped with an invoice — including when an invoice
        is regenerated, which clears the stamp on its rows.
      </p>

      <ToastContainer toasts={toasts} onClose={removeToast} onRemove={removeToast} />
    </div>
  )
}
